"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Bell,
  Check,
  ChevronRightCircle,
  Dot,
  LogOut,
  MailWarning,
  MessageCircle,
  MessageSquareMore,
  Newspaper,
  Radio,
  Search,
  Settings,
  Shield,
  Sparkles,
  UserCircle,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useEffectEvent, useRef, useState, useTransition } from "react";
import { logoutAction } from "@/lib/admin-actions";
import { identifyAnalyticsUser, resetAnalyticsUser, trackNotificationClick, trackSearch } from "@/lib/analytics";
import { markAllNotificationsRead, markNotificationReadAction } from "@/lib/comment-actions";
import { FavoriteTeamQuickChip } from "@/components/site/favorite-team-quick-chip";
import { LasztiLogo } from "@/components/site/laszti-logo";
import { leagueHref, teamHref } from "@/lib/football-routes";
import { publicUserName } from "@/lib/user-display";

function initialsFor(name: string | null) {
  return (name ?? "U")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function HeaderTextLink({
  href,
  icon: Icon,
  label,
  children,
  iconOnly = false,
}: {
  href: string;
  icon: LucideIcon;
  label?: string;
  children: React.ReactNode;
  iconOnly?: boolean;
}) {
  return (
    <Link
      href={href}
      title={label}
      aria-label={label}
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 text-[11px] font-black uppercase text-[#1a1a1a] transition-colors hover:text-[#A4243B] sm:h-10 sm:w-10 sm:gap-2 sm:text-sm"
    >
      <Icon size={17} aria-hidden="true" />
      <span className={iconOnly ? "sr-only" : "hidden sm:inline"}>{children}</span>
    </Link>
  );
}

function AccountMenuLink({
  href,
  icon: Icon,
  onClick,
  children,
  badge,
}: {
  href: string;
  icon: LucideIcon;
  onClick?: () => void;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-[#1A1A1A] transition hover:bg-[#F6F1E3] hover:text-[#A4243B]"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-black/[0.08] bg-[#F6F1E3] text-[#A4243B]">
        <Icon size={17} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {badge ? <span className="shrink-0">{badge}</span> : null}
      <ChevronRightCircle size={15} className="text-zinc-300" aria-hidden="true" />
    </Link>
  );
}

function GuestAuthDropdown({
  open,
  onOpenChange,
  onToggle,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onToggle?: () => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useHeaderDropdownDismiss(rootRef, open, () => onOpenChange(false));

  return (
    <div ref={rootRef} className="relative z-[999]">
      <button
        type="button"
        title="Fiók"
        aria-label="Fiók menü"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle ?? (() => onOpenChange(!open))}
        className="grid h-9 w-9 cursor-pointer place-items-center rounded-full border border-transparent text-[#1a1a1a] transition hover:border-black/[0.08] hover:bg-[#F6F1E3] hover:text-[#A4243B] sm:h-10 sm:w-10"
      >
        <UserRound size={18} aria-hidden="true" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-[999] mt-3 w-44 rounded-xl border border-black/[0.08] bg-white p-1.5 text-[#1A1A1A] shadow-[0_14px_34px_rgba(17,17,17,0.10)]"
        >
          <div className={dropdownCaretClassName()} aria-hidden="true" />
          <Link
            href="/login"
            role="menuitem"
            onClick={() => onOpenChange(false)}
            className="block rounded-lg px-3 py-2.5 text-sm font-bold transition hover:bg-[#F6F1E3] hover:text-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
          >
            Belépés
          </Link>
          <Link
            href="/register"
            role="menuitem"
            onClick={() => onOpenChange(false)}
            className="block rounded-lg px-3 py-2.5 text-sm font-bold transition hover:bg-[#F6F1E3] hover:text-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
          >
            Regisztráció
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function dropdownCaretClassName() {
  return "absolute -top-1.5 right-4 h-3 w-3 rotate-45 border-l border-t border-black/[0.08] bg-white";
}

function useHeaderDropdownDismiss(
  rootRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
) {
  const handlePointerDown = useEffectEvent((event: PointerEvent) => {
    if (!rootRef.current?.contains(event.target as Node)) {
      onClose();
    }
  });
  const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    }
  });

  useEffect(() => {
    if (!open) return;
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);
}

function AccountDropdown({
  open,
  onOpenChange,
  onToggle,
  viewerRole,
  viewerName,
  viewerUsername,
  viewerEmail,
  viewerEmailVerified,
  unreadNotifications,
  showNotificationBadge = false,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onToggle?: () => void;
  viewerRole: string | null;
  viewerName: string | null;
  viewerUsername: string | null;
  viewerEmail: string | null;
  viewerEmailVerified: boolean;
  unreadNotifications: number;
  showNotificationBadge?: boolean;
}) {
  const canEdit = viewerRole === "ADMIN" || viewerRole === "EDITOR";
  const initials = initialsFor(viewerName);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useHeaderDropdownDismiss(rootRef, open, () => onOpenChange(false));

  return (
    <div ref={rootRef} className="relative z-[999]">
      <button
        type="button"
        title={viewerName ?? "Profil"}
        aria-label="Profil menü"
        aria-expanded={open}
        onClick={onToggle ?? (() => onOpenChange(!open))}
        className="relative grid h-9 w-9 cursor-pointer place-items-center rounded-full border border-transparent text-[#1a1a1a] transition hover:border-black/[0.08] hover:bg-[#F6F1E3] hover:text-[#A4243B] sm:h-10 sm:w-10"
      >
        <UserCircle size={20} aria-hidden="true" />
        {showNotificationBadge && unreadNotifications > 0 ? (
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#A4243B] ring-2 ring-white" aria-hidden="true" />
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-[999] mt-3 w-[min(340px,calc(100vw-1.5rem))] rounded-xl border border-black/[0.08] bg-white p-3 text-[#1A1A1A] shadow-lg">
          <div className={dropdownCaretClassName()} aria-hidden="true" />
          <div className="mb-2 flex items-center gap-3 rounded-xl border border-black/[0.08] bg-[#F6F1E3] p-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#A4243B] text-sm font-black uppercase text-white">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-black text-[#1A1A1A]">{viewerName}</p>
              <p data-ph-mask className="truncate text-xs font-semibold text-zinc-500">{viewerEmail}</p>
            </div>
          </div>
          {!viewerEmailVerified ? (
            <Link
              href="/settings?tab=profile"
              onClick={() => onOpenChange(false)}
              className="mb-2 flex items-center gap-2 rounded-lg border border-[#A4243B]/15 bg-[#fff7f4] px-3 py-2 text-xs font-black uppercase text-[#A4243B] transition hover:border-[#A4243B]/35"
            >
              <MailWarning size={15} aria-hidden="true" />
              Email nincs megerősítve
            </Link>
          ) : null}

          <nav aria-label="Profil gyorsmenü" className="grid gap-1">
            <AccountMenuLink href="/settings?tab=profile" icon={UserCircle} onClick={() => onOpenChange(false)}>
              Profil beállítások
            </AccountMenuLink>
            {viewerUsername ? (
              <AccountMenuLink href={`/u/${viewerUsername}`} icon={MessageCircle} onClick={() => onOpenChange(false)}>
                Fórumprofil
              </AccountMenuLink>
            ) : null}
            <AccountMenuLink href="/settings" icon={Settings} onClick={() => onOpenChange(false)}>
              Beállítások
            </AccountMenuLink>
            <AccountMenuLink
              href="/notifications"
              icon={Bell}
              onClick={() => onOpenChange(false)}
              badge={
                showNotificationBadge && unreadNotifications > 0 ? (
                  <span className="rounded-full bg-[#A4243B] px-2 py-0.5 text-[11px] font-black text-white">
                    {unreadNotifications}
                  </span>
                ) : null
              }
            >
              Értesítések
            </AccountMenuLink>
            {canEdit ? (
              <AccountMenuLink href="/admin" icon={Shield} onClick={() => onOpenChange(false)}>
                Admin
              </AccountMenuLink>
            ) : null}
          </nav>

          <form action={logoutAction} className="mt-2 border-t border-black/[0.08] pt-2">
            <button
              onClick={resetAnalyticsUser}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-bold text-red-600 transition hover:bg-red-50"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-red-100 bg-red-50 text-red-600">
                <LogOut size={17} aria-hidden="true" />
              </span>
              Kijelentkezés
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

type QuickSearchResults = {
  articles: { id: string; title: string; slug: string; category: { name: string } }[];
  threads: { id: string; title: string; href: string; forumName: string; isLive: boolean }[];
  teams: { id: string; name: string; slug: string; league: { slug: string } | null }[];
  leagues: { id: string; name: string; slug: string }[];
  users: { id: string; username: string; displayName: string | null }[];
};

type QuickSearchItem = {
  key: string;
  href: string;
  label: string;
  meta: string;
  searchTerm: string;
};

const emptyQuickSearchResults: QuickSearchResults = {
  articles: [],
  threads: [],
  teams: [],
  leagues: [],
  users: [],
};

export type HeaderNotificationItem = {
  id: string;
  type: string;
  targetType: string | null;
  targetId: string | null;
  href: string;
  title: string;
  preview: string;
  createdAt: string;
  read: boolean;
};

export type HeaderFavoriteTeam = {
  name: string;
  href: string;
  logoUrl: string | null;
  externalTeam?: { crest: string | null } | null;
};

export type HeaderViewerData = {
  viewerId: string | null;
  viewerRole: string | null;
  viewerName: string | null;
  viewerUsername: string | null;
  viewerEmail: string | null;
  viewerEmailVerified: boolean;
  unreadNotifications: number;
  latestNotifications: HeaderNotificationItem[];
  favoriteTeam: HeaderFavoriteTeam | null;
};

const emptyViewerData: HeaderViewerData = {
  viewerId: null,
  viewerRole: null,
  viewerName: null,
  viewerUsername: null,
  viewerEmail: null,
  viewerEmailVerified: true,
  unreadNotifications: 0,
  latestNotifications: [],
  favoriteTeam: null,
};

function SearchDropdown({
  trends,
  favoriteTeam,
  open,
  onOpenChange,
}: {
  trends: string[];
  favoriteTeam: { name: string; href: string; logoUrl: string | null; externalTeam?: { crest: string | null } | null } | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<QuickSearchResults>(emptyQuickSearchResults);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(window.localStorage.getItem("laszti:recent-searches") ?? "[]");
    } catch {
      return [];
    }
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  useHeaderDropdownDismiss(rootRef, open, () => onOpenChange(false));

  useEffect(() => {
    if (query.trim().length < 2) {
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`, {
          cache: "no-store",
          headers: { accept: "application/json" },
          signal: controller.signal,
        });
        if (!response.ok) return;
        const next = (await response.json()) as QuickSearchResults;
        setResults(next);
      } catch {
        if (!controller.signal.aborted) setResults(emptyQuickSearchResults);
      }
    }, 180);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [query]);

  const trimmedQuery = query.trim();
  const visibleResults = trimmedQuery.length < 2 ? emptyQuickSearchResults : results;
  const quickResults: QuickSearchItem[] = [
    ...visibleResults.articles.map((article) => ({
      key: `article-${article.id}`,
      href: `/article/${article.slug}`,
      label: article.title,
      meta: article.category.name,
      searchTerm: trimmedQuery,
    })),
    ...visibleResults.threads.map((thread) => ({
      key: `thread-${thread.id}`,
      href: thread.href,
      label: thread.title,
      meta: `${thread.forumName}${thread.isLive ? " · LIVE" : ""}`,
      searchTerm: trimmedQuery,
    })),
    ...visibleResults.teams.map((team) => ({
      key: `team-${team.id}`,
      href: teamHref(team.league?.slug, team.slug),
      label: team.name,
      meta: "Csapat",
      searchTerm: trimmedQuery,
    })),
    ...visibleResults.leagues.map((league) => ({
      key: `league-${league.id}`,
      href: leagueHref(league.slug),
      label: league.name,
      meta: "Liga",
      searchTerm: trimmedQuery,
    })),
    ...visibleResults.users.map((user) => ({
      key: `user-${user.id}`,
      href: `/u/${user.username}`,
      label: publicUserName(user),
      meta: "User",
      searchTerm: trimmedQuery,
    })),
  ];
  const totalVisibleResults = quickResults.length;

  function remember(term: string) {
    if (!term) {
      onOpenChange(false);
      return;
    }
    trackSearch({
      query: term,
      resultCount: totalVisibleResults,
      source: "header",
      hasResults: totalVisibleResults > 0,
    });
    const next = [term, ...recentSearches.filter((item) => item !== term)].slice(0, 6);
    setRecentSearches(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("laszti:recent-searches", JSON.stringify(next));
    }
    onOpenChange(false);
  }

  function searchHref(term: string) {
    return `/search?q=${encodeURIComponent(term)}`;
  }

  function navigateTo(href: string, term: string) {
    remember(term);
    router.push(href);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!trimmedQuery) return;
    const highlightedResult = highlightedIndex >= 0 ? quickResults[highlightedIndex] : null;
    navigateTo(highlightedResult?.href ?? searchHref(trimmedQuery), highlightedResult?.searchTerm ?? trimmedQuery);
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!quickResults.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => (index + 1) % quickResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => (index <= 0 ? quickResults.length - 1 : index - 1));
    }
  }

  return (
    <div ref={rootRef} className="relative z-[999]">
      <button
        type="button"
        title="Keresés"
        aria-label="Keresés"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="grid h-9 w-9 cursor-pointer place-items-center rounded-full border border-transparent text-[#1a1a1a] transition hover:border-black/[0.08] hover:bg-[#F6F1E3] hover:text-[#A4243B] sm:h-10 sm:w-10"
      >
        <Search size={17} aria-hidden="true" />
      </button>
      {open ? (
        <div className="absolute right-0 top-full mt-3 w-[min(420px,calc(100vw-1rem))] rounded-xl border border-black/[0.08] bg-white p-3 shadow-lg">
          <div className={dropdownCaretClassName()} aria-hidden="true" />
          <form action="/search" className="grid gap-3" onSubmit={handleSubmit}>
            <input
              name="q"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Keresés: cikk, thread, csapat, liga, user"
              className="h-11 rounded-lg border border-zinc-300 px-3 text-sm font-semibold text-[#1A1A1A] outline-none transition focus:border-[#A4243B]"
            />
            <FavoriteTeamQuickChip team={favoriteTeam} onUse={() => onOpenChange(false)} />
          </form>

          {query.trim().length < 2 ? (
            <div className="mt-3 grid gap-4">
              {recentSearches.length ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-zinc-500">Recent</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {recentSearches.map((term) => (
                      <Link
                        key={term}
                        href={searchHref(term)}
                        onClick={() => remember(term)}
                        className="rounded-full border border-[#E7E1D7] bg-[#F7F5F1] px-3 py-1 text-sm font-bold text-[#5F666B] transition hover:border-[#D8D0C3] hover:bg-[#F1ECE4] hover:text-[#1A1A1A]"
                      >
                        {term}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-zinc-500">Trending</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {trends.map((term) => (
                    <Link
                      key={term}
                      href={searchHref(term)}
                      onClick={() => remember(term)}
                      className="rounded-full border border-[#E7E1D7] bg-[#F7F5F1] px-3 py-1 text-sm font-bold text-[#5F666B] transition hover:border-[#D8D0C3] hover:bg-[#F1ECE4] hover:text-[#1A1A1A]"
                    >
                      {term}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-3 grid gap-3">
              {quickResults.map((result, index) => (
                <Link
                  key={result.key}
                  href={result.href}
                  onClick={() => remember(result.searchTerm)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onFocus={() => setHighlightedIndex(index)}
                  className={`rounded-lg border px-3 py-3 transition ${
                    highlightedIndex === index
                      ? "border-[#A4243B]/35 bg-[#F1ECE4]"
                      : "border-[#E7E1D7] bg-[#F7F5F1] hover:border-[#D8D0C3] hover:bg-[#F1ECE4]"
                  }`}
                >
                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#5F666B]">{result.meta}</p>
                  <p className="mt-1 text-sm font-black text-[#1A1A1A]">{result.label}</p>
                </Link>
              ))}
              {totalVisibleResults === 0 ? (
                <p className="rounded-lg bg-[#F6F1E3] px-3 py-3 text-sm font-semibold text-zinc-600">
                  Nincs gyors találat. Nyomj Entert a teljes keresési oldalhoz.
                </p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function notificationIcon(type: string) {
  if (type.startsWith("FRIEND_")) return UserRound;
  if (type.includes("MENTION")) return MessageSquareMore;
  if (type.includes("REPLY")) return MessageCircle;
  if (type.includes("TRENDING")) return Sparkles;
  if (type.includes("THREAD")) return Radio;
  if (type.includes("COMMENT")) return Newspaper;
  return Bell;
}

function notificationGroupLabel(date: Date) {
  const now = new Date();
  const target = new Date(date);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const itemDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const dayDiff = Math.round((today.getTime() - itemDay.getTime()) / 86400000);

  if (dayDiff <= 0) return "Ma";
  if (dayDiff === 1) return "Tegnap";
  if (dayDiff < 7) return "A héten";
  return "Korábban";
}

function relativeTimeLabel(value: string) {
  const date = new Date(value);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const ranges = [
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
  ] as const;

  for (const range of ranges) {
    if (Math.abs(diffSeconds) >= range.seconds) {
      return new Intl.RelativeTimeFormat("hu-HU", { numeric: "auto" }).format(
        Math.round(diffSeconds / range.seconds),
        range.unit,
      );
    }
  }

  return "épp most";
}

function NotificationDropdown({
  open,
  onOpenChange,
  unreadNotifications,
  latestNotifications,
  onMarkAllRead,
  markAllReadPending,
  notificationError,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  unreadNotifications: number;
  latestNotifications: HeaderNotificationItem[];
  onMarkAllRead: () => void;
  markAllReadPending: boolean;
  notificationError: string | null;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useHeaderDropdownDismiss(rootRef, open, () => onOpenChange(false));

  const groupedNotifications = latestNotifications.reduce<Array<{ label: string; items: HeaderNotificationItem[] }>>(
    (groups, item) => {
      const label = notificationGroupLabel(new Date(item.createdAt));
      const existing = groups.find((group) => group.label === label);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
      return groups;
    },
    [],
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        title="Értesítések"
        aria-label={`Értesítések${unreadNotifications ? `, ${unreadNotifications} olvasatlan` : ""}`}
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#1a1a1a] transition-colors hover:bg-[#F6F1E3] hover:text-[#A4243B] sm:h-10 sm:w-10"
      >
        <Bell size={17} aria-hidden="true" />
        {unreadNotifications > 0 ? (
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#A4243B] ring-2 ring-white" aria-hidden="true" />
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-[999] mt-3 box-border w-[min(22rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] overflow-x-hidden overflow-y-hidden rounded-xl border border-black/[0.08] bg-white text-[#1A1A1A] shadow-lg">
          <div className={dropdownCaretClassName()} aria-hidden="true" />
          <div className="flex min-w-0 items-center justify-between gap-2 border-b border-black/[0.07] px-3 py-2.5">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <p className="truncate text-sm font-black text-[#1A1A1A]">Értesítések</p>
              <span className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-bold text-zinc-600">
                {unreadNotifications > 0 ? `${unreadNotifications} olvasatlan` : "0 olvasatlan"}
              </span>
            </div>
            {unreadNotifications > 0 ? (
              <div className="shrink-0">
                <button
                  type="button"
                  title="Összes értesítés olvasottnak jelölése"
                  aria-label="Összes értesítés olvasottnak jelölése"
                  disabled={markAllReadPending}
                  onClick={onMarkAllRead}
                  className="whitespace-nowrap rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[11px] font-bold text-zinc-600 transition hover:border-black/[0.12] hover:bg-zinc-50 hover:text-[#1A1A1A] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {markAllReadPending ? "Jelölés..." : "Összes olvasott"}
                </button>
              </div>
            ) : null}
          </div>
          {notificationError ? (
            <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{notificationError}</p>
          ) : null}

          <div className="max-h-[min(68vh,26rem)] overflow-y-auto overflow-x-hidden p-2">
            {groupedNotifications.length > 0 ? (
              groupedNotifications.map((group) => (
                <section key={group.label} className="pb-2">
                  <p className="px-2 pb-1.5 pt-2.5 text-[10px] font-black uppercase tracking-[0.14em] text-zinc-400 first:pt-1">
                    {group.label}
                  </p>
                  <div className="grid min-w-0 gap-1">
                    {group.items.map((notification) => {
                      const Icon = notificationIcon(notification.type);
                      return (
                        <div
                          key={notification.id}
                          className={`group relative rounded-lg border transition-all ${
                            notification.read
                              ? "border-transparent bg-transparent hover:border-black/[0.08] hover:bg-zinc-50 hover:shadow-[0_6px_16px_rgba(17,17,17,0.06)]"
                              : "border-black/[0.06] bg-white hover:border-black/[0.1] hover:bg-zinc-50 hover:shadow-[0_6px_16px_rgba(17,17,17,0.06)]"
                          }`}
                        >
                          {!notification.read ? <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-[#A4243B]/70" aria-hidden="true" /> : null}
                          <div className="flex min-w-0 max-w-full items-start gap-2 overflow-hidden px-2.5 py-[9px]">
                            <Link
                              href={notification.href}
                              onClick={() => {
                                trackNotificationClick({
                                  notificationType: notification.type,
                                  targetType: notification.targetType,
                                  targetId: notification.targetId,
                                });
                                onOpenChange(false);
                              }}
                              className="flex min-w-0 flex-[1_1_0%] items-start gap-2 overflow-hidden rounded-lg outline-none transition group-hover:text-[#1A1A1A] focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
                            >
                              <span
                                className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border ${
                                  notification.read
                                    ? "border-black/[0.07] bg-zinc-50 text-zinc-500"
                                    : "border-[#A4243B]/10 bg-zinc-50 text-[#A4243B]"
                                }`}
                              >
                                <Icon size={14} aria-hidden="true" />
                              </span>

                              <span className="block min-w-0 flex-1 overflow-hidden">
                                <span className="flex min-w-0 max-w-full items-start gap-1.5 overflow-hidden">
                                  {!notification.read ? (
                                    <Dot size={14} className="-ml-0.5 mt-0.5 shrink-0 text-[#A4243B]" aria-hidden="true" />
                                  ) : null}
                                  <span className="block min-w-0 max-w-full flex-[1_1_0%] whitespace-normal break-words text-[13px] font-bold leading-5 text-[#1A1A1A] [overflow-wrap:anywhere]">
                                    {notification.title}
                                  </span>
                                </span>
                                {notification.preview ? (
                                  <span
                                    className="mt-0.5 block min-w-0 max-w-full overflow-hidden whitespace-normal break-words text-xs font-medium leading-4 text-zinc-500 [overflow-wrap:anywhere]"
                                    style={{
                                      display: "-webkit-box",
                                      WebkitBoxOrient: "vertical",
                                      WebkitLineClamp: 2,
                                    }}
                                  >
                                    {notification.preview}
                                  </span>
                                ) : null}
                                <span className="mt-1.5 block min-w-0 whitespace-normal break-words text-[11px] font-semibold text-zinc-400 [overflow-wrap:anywhere]">
                                  {relativeTimeLabel(notification.createdAt)}
                                </span>
                              </span>
                            </Link>

                            {!notification.read ? (
                              <form action={markNotificationReadAction} className="shrink-0">
                                <input type="hidden" name="notificationId" value={notification.id} />
                                <button
                                  type="submit"
                                  aria-label="Olvasottnak jelölés"
                                  className="grid h-7 w-7 place-items-center rounded-full text-zinc-400 transition hover:bg-white hover:text-[#1A1A1A]"
                                >
                                  <Check size={13} aria-hidden="true" />
                                </button>
                              </form>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            ) : (
              <div className="p-4">
                <p className="rounded-xl border border-dashed border-black/[0.08] bg-zinc-50 px-4 py-6 text-center text-sm font-semibold text-zinc-500">
                  Nincs új értesítés.
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-black/[0.07] bg-zinc-50/80 p-1.5">
            <Link
              href="/notifications"
              onClick={() => onOpenChange(false)}
              className="flex items-center justify-between rounded-xl px-3 py-2 text-[13px] font-bold text-[#1A1A1A] transition hover:bg-white hover:text-[#A4243B]"
            >
              <span>Összes értesítés</span>
              <ChevronRightCircle size={15} aria-hidden="true" />
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function HeaderClient({
  viewer,
  liveThreadCount,
  searchTrends,
}: {
  viewer?: HeaderViewerData | null;
  liveThreadCount: number;
  searchTrends: string[];
}) {
  const [viewerData, setViewerData] = useState<HeaderViewerData>(viewer ?? emptyViewerData);
  const {
    viewerId,
    viewerRole,
    viewerName,
    viewerUsername,
    viewerEmail,
    viewerEmailVerified,
    unreadNotifications,
    latestNotifications,
    favoriteTeam,
  } = viewerData;
  const isLoggedIn = viewerRole !== null;
  const [openPanel, setOpenPanel] = useState<"mobileSearch" | "desktopSearch" | "notifications" | "account" | "guestAuth" | null>(null);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
  const [optimisticUnreadNotifications, setOptimisticUnreadNotifications] = useState(unreadNotifications);
  const [optimisticLatestNotifications, setOptimisticLatestNotifications] = useState(latestNotifications);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [markAllReadPending, startMarkAllReadTransition] = useTransition();
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    async function loadViewer() {
      try {
        const response = await fetch("/api/header-viewer", {
          cache: "no-store",
          headers: { accept: "application/json" },
          signal: controller.signal,
        });
        if (!response.ok) return;
        const next = (await response.json()) as HeaderViewerData;
        setViewerData(next);
        setOptimisticUnreadNotifications(next.unreadNotifications);
        setOptimisticLatestNotifications(next.latestNotifications);
      } catch {
        if (!controller.signal.aborted) {
          setViewerData(emptyViewerData);
          setOptimisticUnreadNotifications(0);
          setOptimisticLatestNotifications([]);
        }
      }
    }

    void loadViewer();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!viewerId) {
      if (identifiedUserIdRef.current) {
        resetAnalyticsUser();
        identifiedUserIdRef.current = null;
      }
      return;
    }

    identifyAnalyticsUser(viewerId, {
      username: viewerUsername,
      role: viewerRole,
      favoriteTeam: favoriteTeam?.name ?? null,
    });
    identifiedUserIdRef.current = viewerId;
  }, [favoriteTeam?.name, viewerId, viewerRole, viewerUsername]);

  function handleMarkAllNotificationsRead() {
    if (optimisticUnreadNotifications <= 0 || markAllReadPending) return;

    const previousUnread = optimisticUnreadNotifications;
    const previousLatest = optimisticLatestNotifications;
    setNotificationError(null);
    setOptimisticUnreadNotifications(0);
    setOptimisticLatestNotifications((items) => items.map((item) => ({ ...item, read: true })));

    startMarkAllReadTransition(async () => {
      try {
        await markAllNotificationsRead();
      } catch (error) {
        setOptimisticUnreadNotifications(previousUnread);
        setOptimisticLatestNotifications(previousLatest);
        setNotificationError(error instanceof Error ? error.message : "Nem sikerült olvasottnak jelölni az értesítéseket.");
      }
    });
  }

  function toggleMobileAccount() {
    setOpenPanel(null);
    setMobileAccountOpen((open) => !open);
  }

  function setMobileAccount(next: boolean) {
    if (next) {
      setOpenPanel(null);
    }
    setMobileAccountOpen(next);
  }

  return (
    <header className="sticky top-0 z-[100] w-full max-w-full overflow-visible border-b border-black/[0.08] bg-white/95 shadow-[0_1px_8px_rgba(39,62,71,0.06)] backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-2 sm:hidden">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Link
            href="/"
            aria-label="Laszti365 főoldal"
            className="inline-flex min-w-0 flex-1 items-center overflow-hidden leading-none"
          >
            <LasztiLogo className="w-[126px] max-w-full" />
          </Link>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-1">
          <Link
            href="/forum"
            className="relative inline-flex h-10 shrink-0 items-center px-1.5 text-[12px] font-black uppercase text-[#1A1A1A] transition hover:text-[#A4243B]"
          >
            FÓRUM
            {liveThreadCount > 0 ? (
              <span className="absolute -right-0.5 top-1 h-2 w-2 rounded-full bg-[#A4243B] ring-2 ring-white" aria-hidden="true" />
            ) : null}
          </Link>
          <SearchDropdown
            trends={searchTrends}
            favoriteTeam={favoriteTeam}
            open={openPanel === "mobileSearch"}
            onOpenChange={(next) => {
              setMobileAccountOpen(false);
              setOpenPanel(next ? "mobileSearch" : null);
            }}
          />
          {isLoggedIn ? (
            <AccountDropdown
              open={mobileAccountOpen}
              onOpenChange={setMobileAccount}
              onToggle={toggleMobileAccount}
              viewerRole={viewerRole}
              viewerName={viewerName}
              viewerUsername={viewerUsername}
              viewerEmail={viewerEmail}
              viewerEmailVerified={viewerEmailVerified}
              unreadNotifications={optimisticUnreadNotifications}
              showNotificationBadge
            />
          ) : (
            <GuestAuthDropdown
              open={mobileAccountOpen}
              onOpenChange={setMobileAccount}
              onToggle={toggleMobileAccount}
            />
          )}
        </div>
      </div>

      <div className="mx-auto hidden max-w-7xl items-center justify-between gap-1.5 px-2 py-0.5 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:gap-4 sm:px-6 sm:py-1 lg:py-1.5">
        <div className="hidden min-w-0 sm:block" aria-hidden="true" />
        <Link
          href="/"
          aria-label="Laszti365 főoldal"
          className="inline-flex min-w-0 shrink items-center justify-start self-center overflow-visible leading-none sm:shrink-0 sm:justify-self-center"
        >
          <LasztiLogo className="w-[150px] min-[380px]:w-[164px] sm:w-[242px] lg:w-[300px]" />
        </Link>

        <div className="flex min-w-0 shrink-0 items-center justify-end gap-4 sm:gap-5 lg:gap-7">
          <SearchDropdown
            trends={searchTrends}
            favoriteTeam={favoriteTeam}
            open={openPanel === "desktopSearch"}
            onOpenChange={(next) => {
              setMobileAccountOpen(false);
              setOpenPanel(next ? "desktopSearch" : null);
            }}
          />
          <div className="relative">
            <HeaderTextLink href="/forum" icon={MessageCircle} label="Te mondd meg">
              Fórum
            </HeaderTextLink>
            {liveThreadCount > 0 ? (
              <span className="absolute -right-3 top-0 rounded-full bg-[#A4243B] px-1.5 py-0.5 text-[10px] font-black uppercase text-white">
                Live
              </span>
            ) : null}
          </div>
          {isLoggedIn ? (
            <>
              <NotificationDropdown
                open={openPanel === "notifications"}
                onOpenChange={(next) => {
                  setMobileAccountOpen(false);
                  setOpenPanel(next ? "notifications" : null);
                }}
                unreadNotifications={optimisticUnreadNotifications}
                latestNotifications={optimisticLatestNotifications}
                onMarkAllRead={handleMarkAllNotificationsRead}
                markAllReadPending={markAllReadPending}
                notificationError={notificationError}
              />
              <AccountDropdown
                open={openPanel === "account"}
                onOpenChange={(next) => {
                  setMobileAccountOpen(false);
                  setOpenPanel(next ? "account" : null);
                }}
                viewerRole={viewerRole}
                viewerName={viewerName}
                viewerUsername={viewerUsername}
                viewerEmail={viewerEmail}
                viewerEmailVerified={viewerEmailVerified}
                unreadNotifications={optimisticUnreadNotifications}
              />
            </>
          ) : (
            <GuestAuthDropdown
              open={openPanel === "guestAuth"}
              onOpenChange={(next) => {
                setMobileAccountOpen(false);
                setOpenPanel(next ? "guestAuth" : null);
              }}
            />
          )}
        </div>
      </div>

    </header>
  );
}
